import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChangeShippingOptionPayload, SubmitOrderPayload } from 'types/orders'
import {
  addToCart,
  addToFavoritesPharmacy,
  calculateEstimate,
  cancelOrder,
  cancelOrderRequest,
  changeShippingOption,
  orderDrugs,
  removeFromCart,
  reOrder,
} from 'utils/api/orders'

export function useSubmitOrders() {
  return useMutation({
    mutationFn: (payload: SubmitOrderPayload) => orderDrugs(payload),
  })
}

export function useChangeShippingOption() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { orderId: string; payload: ChangeShippingOptionPayload }) =>
      changeShippingOption(payload.orderId, payload.payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [`/orders/${res.id}/details`] })
    },
  })
}

export function useAddFavoritePharmacy() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (pharmacyId: string) => addToFavoritesPharmacy(pharmacyId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [`providers`, `all`, `pharmacy`] })
      queryClient.invalidateQueries({ queryKey: [`/orders/${res.id}/details`] })
    },
  })
}

export function useCancelOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => cancelOrder(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [`orders`] })
      queryClient.invalidateQueries({ queryKey: [`/orders/${res.id}/details`] })
    },
  })
}

export function useRepeatOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => reOrder(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [`orders`] })
      queryClient.invalidateQueries({ queryKey: [`/orders/${res.id}/details`] })
    },
  })
}

export function useAddToCart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { id: string; items: SubmitOrderPayload['items'] }) => addToCart(payload),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: [`orders`] })
      queryClient.invalidateQueries({ queryKey: [`/orders/${res.id}/details`] })
      queryClient.invalidateQueries({ queryKey: [`/orders/${res.id}/details`, res.id] })
      res.order_items?.map((item: any) => {
        queryClient.invalidateQueries({
          queryKey: [`/orders/${item.order_id}/details`, item.order_id],
        })
      })
    },
  })
}

export function useRemoveFromCart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { id: string; items: any[] }) => removeFromCart(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [`orders`] })
      queryClient.invalidateQueries({ queryKey: [`/orders/${res.id}/details`] })
      queryClient.invalidateQueries({ queryKey: [`/orders/${res.id}/details`, res.id] })
    },
  })
}

export function useCancelOrderRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => cancelOrderRequest(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [`orders`] })
      queryClient.invalidateQueries({ queryKey: [`/orders/${res.id}/details`] })
    },
  })
}

export function useCalculateOrderEstimate() {
  return useMutation({
    mutationFn: (payload: SubmitOrderPayload) => calculateEstimate(payload),
  })
}
